import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";

// APIs & Hooks
import { baseAxios } from "../apis";
import { useCsrfToken } from "../hooks/useCsrfToken";
import { useToast } from "./toastContext";

// Components
import DialogSuccess from "../components/dialogs/DialogSuccess";
import DialogAsk from "../components/dialogs/DialogAsk";

// Assets
import {
  IllustrationBotStop,
  IllustrationConfirmedDocument,
} from "../assets/Illustrations";

// Utils
import { extractErrorMessage } from "../utils";
import { useAuth } from "./authContext";
import DrawerActivateBotDemo from "../components/drawersDemo/DrawerActivateBotDemo";
import DrawerStopBotDemo from "../components/drawersDemo/DrawerStopBotDemo";
import DrawerStopBotConvertDemo from "../components/drawersDemo/DrawerStopBotConvertDemo";
import { useExchangesQuery } from "../hooks/useBotQueries";

// 1. Create The Context
const BotContext = createContext();

// 2. Create The Provider Component
export const BotDemoProvider = ({ children }) => {
  // Custom Hooks
  const { fetchCsrfToken } = useCsrfToken();
  const { addToast } = useToast();
  const { user } = useAuth();

  // Dialog & Selection State
  const [openDialogBot, setOpenDialogBot] = useState(null);
  const [selectedBot, setSelectedBot] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedExchange, setSelectedExchange] = useState(null);

  // Loading State (Reverted to original structure)
  const [loadingBotLists, setLoadingBotLists] = useState(false);
  const [loadingBotDetail, setLoadingBotDetail] = useState(false);
  const [loadingStopStartBot, setLoadingStopStartBot] = useState(false);
  const [loadingConvertData, setLoadingConvertData] = useState(false);
  const [loadingGetExchange, setLoadingGetExchange] = useState(false);
  const [loadingBotProfile, setLoadingBotProfile] = useState(false);
  const [loadingDisconnectExchange, setLoadingDisconnectExchange] =
    useState(false);

  // Data State
  const [strategyLists, setStrategyLists] = useState([]);
  const [botLists, setBotLists] = useState([]);
  const [botDetail, setBotDetail] = useState(null);
  const [dataChart, setDataChart] = useState([]);
  const [convertData, setConvertData] = useState(null);
  const [exchanges, setExchanges] = useState([]);
  const [myExchanges, setMyExchanges] = useState([]);
  const [botProfiles, setBotProfiles] = useState({});

  // setting Bot
  const [selectedSetting, setSelectedSetting] = useState(null);

  // Reset All
  const [loadingResetAll, setLoadingResetAll] = useState(false);

  // Memoized Error Handler
  const handleApiError = useCallback(
    (error) => {
      const errorMessage = extractErrorMessage(error?.response?.data || error);
      addToast("error", errorMessage, 10000);
    },
    [addToast],
  );

  // Memoized API Call wrappers
  const authedPost = useCallback(
    async (url, payload = {}) => {
      const token = await fetchCsrfToken();
      return baseAxios.post(url, payload, {
        headers: { "X-CSRFToken": token },
      });
    },
    [fetchCsrfToken],
  );

  const authedDelete = useCallback(
    async (url) => {
      const token = await fetchCsrfToken();
      return baseAxios.delete(url, {
        headers: { "X-CSRFToken": token },
      });
    },
    [fetchCsrfToken],
  );

  // Data Fetching Functions
  const fetchStrategyLists = useCallback(async () => {
    if (!user || strategyLists.length > 0) return;
    try {
      // const { data } = await baseAxios.get("/api/strategy-lists");
      const { data } = await baseAxios.get("/api/demo/bot-masters");

      setStrategyLists(data.bot_master_list);
    } catch (err) {
      // handleApiError(err);
    }
  }, [user, strategyLists.length]);

  const fetchBotListsDemo = useCallback(
    async (isUpdate = false) => {
      if (!user || (botLists?.length && !isUpdate)) return;
      setLoadingBotLists(true);
      try {
        const { data } = await baseAxios.get("/api/demo/bot_list");
        console.log("data", data);

        setBotLists(data.bot_lists);
      } catch (err) {
        handleApiError(err);
      } finally {
        setLoadingBotLists(false);
      }
    },
    [user, botLists?.length, handleApiError],
  );

  const fetchBotDetailDemo = useCallback(
    async (id) => {
      if (!id) return;
      setLoadingBotDetail(true);
      setBotDetail(null);
      setDataChart([]);
      try {
        const { data } = await baseAxios.get(`/api/demo/bot_detail/${id}`);
        setDataChart(data?.graph_data || []);
        setBotDetail({ ...data.bot_detail, setting: data?.setting });
        const foundStrategy = strategyLists.find(
          (strg) => strg.bot_name === data.bot_detail.strategy_name,
        );
        setSelectedStrategy(foundStrategy);
      } catch (err) {
        handleApiError(err);
      } finally {
        setLoadingBotDetail(false);
      }
    },
    [strategyLists, handleApiError],
  );

  const { data, refetch } = useExchangesQuery();

  const fetchExchangesDemo = useCallback(
    async (isUpdate = false) => {
      if (!user || (!isUpdate && exchanges?.length)) return;
      if (!data) return;

      setLoadingGetExchange(true);

      try {
        // Just take from React Query
        setExchanges(data.exchanges);
        setMyExchanges(data.myExchanges);
      } catch (err) {
        console.log(err);
      } finally {
        setLoadingGetExchange(false);
      }
    },
    [user, data, exchanges?.length],
  );

  // Action Handlers
  const handleStopBotDemo = useCallback(
    async (method, payload, callback) => {
      setLoadingStopStartBot(true);
      const stoppedBotId = botDetail?.id; // capture before async ops
      try {
        await authedPost(
          `/api/demo/stop_bot/${stoppedBotId}/?method=${method}`,
          payload,
        );

        // 1. Fetch the updated bot list first
        let updatedList = [];
        try {
          const { data } = await baseAxios.get("/api/demo/bot_list");
          updatedList = data.bot_lists || [];
          setBotLists(updatedList);
        } catch (err) {
          handleApiError(err);
        }

        // 2. Check if the stopped bot still exists in the new list
        const botStillExists = updatedList.some((b) => b.id === stoppedBotId);

        if (botStillExists) {
          // Bot still in list (e.g. status changed) — refresh its detail
          await fetchBotDetailDemo(stoppedBotId);
        } else {
          // Bot was removed — select the first available bot, or clear
          const nextBot = updatedList.length > 0 ? updatedList[0] : null;
          setSelectedBot(nextBot);
          if (!nextBot) {
            setBotDetail(null);
            setDataChart([]);
          }
          // fetchBotDetailDemo will fire via the selectedBot useEffect automatically
        }

        setOpenDialogBot(
          method === "keep"
            ? "success-stop-bot-keep"
            : "success-stop-bot-convert",
        );
        callback?.();
      } catch (err) {
        handleApiError(err);
      } finally {
        setLoadingStopStartBot(false);
      }
    },
    [botDetail?.id, authedPost, fetchBotDetailDemo, handleApiError],
  );

  const handleStartBotDemo = useCallback(async () => {
    setLoadingStopStartBot(true);
    try {
      await authedPost(`/api/demo/start_bot/${botDetail?.id}/`);
      await fetchBotDetailDemo(botDetail?.id);
      await fetchBotListsDemo(true);
      setOpenDialogBot("success-start-bot");
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingStopStartBot(false);
    }
  }, [
    botDetail?.id,
    authedPost,
    fetchBotDetailDemo,
    fetchBotListsDemo,
    handleApiError,
  ]);

  const handleDisconnectExchangeDemo = useCallback(async () => {
    setLoadingDisconnectExchange(true);
    try {
      const settingId = selectedExchange?.user_api_settings[0]?.id;
      await authedDelete(`/api/demo/delete_api_setting/${settingId}`);
      setOpenDialogBot("success-disconnect-exchange");
      await fetchExchangesDemo(true);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingDisconnectExchange(false);
    }
  }, [selectedExchange, authedDelete, fetchExchangesDemo, handleApiError]);

  const authedGet = useCallback(
    async (url) => {
      const token = await fetchCsrfToken();
      return baseAxios.get(url, {
        headers: { "X-CSRFToken": token },
      });
    },
    [fetchCsrfToken],
  );

  const handleResetAllDemo = useCallback(async () => {
    setLoadingResetAll(true);
    try {
      await authedGet("/api/demo/reset_all/");
      // Setelah berhasil, panggil ulang data yang relevan
      // await fetchBotListsDemo(true);
      // await fetchExchangesDemo(true);
      // Tampilkan dialog sukses
      setBotLists([]);
      setBotDetail(null);
      setOpenDialogBot("success-reset");
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingResetAll(false);
    }
  }, [authedGet, fetchBotListsDemo, fetchExchangesDemo, handleApiError]);

  // Side Effects
  // useEffect(() => {
  //   if (selectedBot) {
  //     fetchBotDetailDemo(selectedBot.id);
  //   } else if (botLists.length > 0) {
  //     setSelectedBot(botLists[0]);
  //   }
  // }, [selectedBot, botLists, fetchBotDetailDemo]);
  // --- Effect 1: Sets the default bot ONCE ---
  useEffect(() => {
    // If we have a list of bots, but no bot is currently selected...
    if (botLists.length > 0 && !selectedBot) {
      // ...select the first one as the default.
      setSelectedBot(botLists[0]);
    }
  }, [botLists, selectedBot]); // This runs only when the list loads or selection is cleared.

  // --- Effect 2: Fetches details ONLY when a bot is selected ---
  useEffect(() => {
    if (selectedBot) {
      // If a bot is selected (either by default or by a user click), fetch its details.
      fetchBotDetailDemo(selectedBot.id);
    } else {
      // IMPORTANT: If no bot is selected, clear the details to prevent showing stale data.
      setBotDetail(null);
      setDataChart([]);
    }
  }, [selectedBot, fetchBotDetailDemo]); // This runs ONLY when `selectedBot` changes.

  useEffect(() => {
    let isMounted = true;
    const fetchConvertData = async () => {
      if (!botDetail?.id) {
        setConvertData(null);
        return;
      }
      setLoadingConvertData(true);
      const currentBotId = botDetail.id;
      try {
        const { data } = await baseAxios.get(
          `/api/demo/stop_bot/convert_details/${currentBotId}`,
        );
        // Only update state if botDetail hasn't changed
        if (isMounted && botDetail?.id === currentBotId) {
          setConvertData(data);
        }
      } catch (err) {
        if (isMounted) {
          setConvertData(null);
          // Optional: handleApiError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingConvertData(false);
        }
      }
    };

    fetchConvertData();

    return () => {
      isMounted = false;
    };
  }, [botDetail?.id]);

  useEffect(() => {
    const fetchBotProfile = async () => {
      if (
        !selectedStrategy?.id ||
        botProfiles?.strategy?.id === selectedStrategy.id
      )
        return;
      setLoadingBotProfile(true);
      try {
        const { data } = await baseAxios.get(
          `/api/bot_profile?bot_master_id=${selectedStrategy.id}`,
        );
        setBotProfiles({
          strategy: selectedStrategy,
          data: data.parameters_level_grouped,
        });
      } catch (err) {
        // handleApiError(err);
      } finally {
        setLoadingBotProfile(false);
      }
    };

    const strategy = selectedStrategy?.bot_name?.toLowerCase();
    if (strategy === "grid" || strategy === "xeno arc") {
      fetchBotProfile();
    }
  }, [selectedStrategy, botProfiles?.strategy?.id, handleApiError]);

  // Dialog Configurations
  const dialogsConfig = [
    {
      type: "stop-bot",
      component: DrawerStopBotDemo,
      props: {
        handleKeepBalance: () => handleStopBotDemo("keep"),
      },
    },
    {
      type: "convert",
      component: DrawerStopBotConvertDemo,
      props: {
        onBack: () => setOpenDialogBot("stop-bot"),
        handleConvert: (payload, callback) =>
          handleStopBotDemo("convert", payload, callback),
        convertData: convertData,
        loading: loadingConvertData,
      },
    },
    {
      type: "disconnect-exchange",
      component: DialogAsk,
      props: {
        title:
          "You’re about to disconnect the exchange. Would you like to continue?",
        loading: loadingDisconnectExchange,
        onConfirm: handleDisconnectExchangeDemo,
      },
    },
    {
      type: "start-bot",
      component: DialogAsk,
      props: {
        title: "You’re about to activate this bot. Would you like to continue?",
        loading: loadingStopStartBot,
        onConfirm: handleStartBotDemo,
      },
    },
    {
      type: "success-disconnect-exchange",
      component: DialogSuccess,
      props: {
        title: "Success! Your exchange has been disconnected.",
      },
    },
    {
      type: "success-stop-bot-convert",
      component: DialogSuccess,
      props: {
        icon: (
          <IllustrationConfirmedDocument className="w-[300px] h-[300px] my-[-4rem] mt-[-5rem]" />
        ),
        title:
          "Bot stop successful – Your request is being processed. Please check your balance regularly.",
      },
    },
    {
      type: "success-stop-bot-keep",
      component: DialogSuccess,
      props: {
        icon: (
          <IllustrationBotStop className="w-[200px] h-[200px] my-[-2rem] mt-[-3rem]" />
        ),
        title: "Bot stop successful – your bot has been stopped!",
      },
    },
    {
      type: "success-start-bot",
      component: DialogSuccess,
      props: {
        title: "Bot start successful – your bot has been started!",
      },
    },
  ];

  const contextValue = {
    // Data
    strategyLists,
    botLists,
    botDetail,
    dataChart,
    convertData,
    exchanges,
    myExchanges,
    systemResults: botProfiles.data,

    // Selections & Dialogs
    selectedBot,
    selectedStrategy,
    selectedExchange,
    selectedSetting,
    openDialogBot,

    // Loading States
    loadingBotLists,
    loadingBotDetail,
    loadingStopStartBot,
    loadingConvertData,
    loadingGetExchange,
    loadingBotProfile,
    loadingDisconnectExchange,
    loadingResetAll,

    // Setters & Handlers
    setSelectedBot,
    setSelectedStrategy,
    setSelectedExchange,
    setOpenDialogBot,
    setSelectedSetting,
    fetchStrategyLists,
    fetchBotListsDemo,
    fetchBotDetailDemo,
    fetchExchangesDemo,
    handleResetAllDemo,
  };

  return (
    <BotContext.Provider value={contextValue}>
      <DrawerActivateBotDemo
        openDialog={openDialogBot}
        botDetail={botDetail}
        setOpenDialog={setOpenDialogBot}
        setSelectedBot={setSelectedStrategy}
        selectedBot={{ ...selectedStrategy, active_bot_id: selectedBot?.id }}
        bots={strategyLists}
      />

      {dialogsConfig.map(({ type, component: Component, props }) => (
        <Component
          key={type}
          open={openDialogBot === type}
          onClose={() => setOpenDialogBot(null)}
          {...props}
        />
      ))}

      {children}
    </BotContext.Provider>
  );
};

// 3. Create a custom hook for easy context consumption
export const useBotDemo = () => {
  const context = useContext(BotContext);
  if (context === undefined) {
    throw new Error("Testing must be used within a BotProvider");
  }
  return context;
};
